import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Highlighter, X } from "lucide-react";
import { toast } from "sonner";
import { removeHighlight, errorDetail } from "@/lib/reader";

export default function ReaderHighlights({ books, highlights, onChanged }) {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);
  const items = books
    .flatMap((b) => (highlights?.[b.id] || []).map((h) => ({ ...h, book: b })))
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const visible = showAll ? items : items.slice(0, 6);

  const remove = async (bookId, id) => {
    try {
      await removeHighlight(bookId, id);
      onChanged();
    } catch (e) {
      toast.error(errorDetail(e));
    }
  };

  return (
    <section className="mt-20" data-testid="reader-highlights">
      <h2 className="font-serif-display text-3xl flex items-center gap-3">
        <Highlighter className="w-6 h-6 text-primary-gold" /> Your Highlights
      </h2>
      {items.length === 0 ? (
        <p className="text-soft text-sm mt-4" data-testid="reader-highlights-empty">
          Select any words while reading and tap Highlight to keep your favourite lines here.
        </p>
      ) : (
        <>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {visible.map((h) => (
              <figure key={h.id} className="group relative border-l-2 border-primary-gold bg-[#121212]/80 pl-6 pr-10 py-5" data-testid={`highlight-item-${h.id}`}>
                <button onClick={() => navigate(`/reader/read/${h.book.id}?page=${h.page}`)} className="text-left w-full" data-testid={`highlight-jump-${h.id}`}>
                  <blockquote className="font-serif-display text-xl leading-relaxed text-text-primary line-clamp-4">“{h.text}”</blockquote>
                  <figcaption className="mt-3 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-soft group-hover:text-primary-gold transition-colors">
                    {h.book.title} · p. {h.page}
                  </figcaption>
                </button>
                <button onClick={() => remove(h.book.id, h.id)} className="absolute top-4 right-3 text-soft hover:text-[#d6453a]" data-testid={`highlight-remove-${h.id}`} aria-label="Remove highlight">
                  <X className="w-4 h-4" />
                </button>
              </figure>
            ))}
          </div>
          {items.length > 6 && (
            <button onClick={() => setShowAll((s) => !s)} data-testid="highlights-toggle-all" className="mt-6 font-mono-ui text-xs uppercase tracking-[0.18em] text-primary-gold hover:text-text-primary">
              {showAll ? "Show fewer" : `Show all ${items.length} highlights`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
